import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AppButton,
  Box,
  EmptyServicesListIcon,
  Inscription,
  List,
  Option,
  PaginationBar,
  Responsive,
  SearchInput,
  SelectDropdown,
  Text,
  useDebounce,
  usePaginationParams,
  useResponsive,
} from '@common-fe/common-fe';

import { IReceiptDtoSourceEnum } from '@/dtos/models';
import useGetReceiptsQuery from '@/modules/MyDocuments/queries/useGetReceipts.query';
import { useAuthStore } from '@/modules/user';
import { useFilters } from '@/utils';

import ReceiptDeleteModal from './ReceiptDeleteModal';
import ReceiptsListItem from './ReceiptsListItem';

const DEFAULT_PAGE_SIZE = 10;
const SEARCH_CHARS_COUNT = 3;

export enum ReceiptsUsage {
  ALL = 'ALL',
  SAVED_FOR_LATER = 'SAVED_FOR_LATER',
  LINKED_TO_CLAIM = 'LINKED_TO_CLAIM',
}

const USAGE_OPTIONS: Option[] = [
  { key: ReceiptsUsage.ALL, value: ReceiptsUsage.ALL, title: 'All' },
  { key: ReceiptsUsage.SAVED_FOR_LATER, value: ReceiptsUsage.SAVED_FOR_LATER, title: 'Saved for Later' },
  { key: ReceiptsUsage.LINKED_TO_CLAIM, value: ReceiptsUsage.LINKED_TO_CLAIM, title: 'Linked to Claim' },
];

export const getAvailableByUsage = (usage: ReceiptsUsage): boolean | undefined => {
  if (usage === ReceiptsUsage.SAVED_FOR_LATER) {
    return true;
  }
  if (usage === ReceiptsUsage.LINKED_TO_CLAIM) {
    return false;
  }
  return undefined;
};

const ReceiptsList = () => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();
  const { page, perPage, setPerPage, setPage, searchString } = usePaginationParams(DEFAULT_PAGE_SIZE);

  const { filtersValues, setFilterValue } = useFilters<{ searchString: string }>(
    { searchString },
    { autoClear: true }
  );
  const debouncedSearch = useDebounce(
    filtersValues.searchString.length >= SEARCH_CHARS_COUNT ? filtersValues.searchString : ''
  );

  const [usage, setUsage] = useState<ReceiptsUsage>(ReceiptsUsage.SAVED_FOR_LATER);
  const available = useMemo(() => getAvailableByUsage(usage), [usage]);

  const { user } = useAuthStore();
  const employeeId = useMemo(() => user?.employee?.id || '', [user?.employee?.id]);

  const { receipts, total, isReceiptsLoading } = useGetReceiptsQuery({
    employeeId,
    page,
    perPage,
    searchString: debouncedSearch,
    available,
    source: IReceiptDtoSourceEnum.RECEIPT_VAULT,
  });

  const [deleteDocumentId, setDeleteDocumentId] = useState<string | undefined>(undefined);

  const handleView = useCallback((id?: string) => {}, []);

  const handleDelete = useCallback((id?: string) => {
    setDeleteDocumentId(id);
  }, []);

  const deletedItem = useMemo(() => {
    return receipts?.find((receipt) => receipt.documentId === deleteDocumentId);
  }, [receipts, deleteDocumentId]);

  const children = useMemo(() => {
    if (isReceiptsLoading) {
      return <List.Preloader testId="receipts-list_preloader" />;
    }

    if (!receipts?.length) {
      return (
        <Box
          height={isMobile ? '360px' : '420px'}
          data-testid="TransactionsNotFound"
          pad={{ top: 'spacing32' }}
          align="center"
        >
          <Box margin={{ bottom: 'spacing24' }}>
            <EmptyServicesListIcon />
          </Box>
          <Inscription
            data-testid="TransactionsNotFound-title"
            color="textDisabled"
            weight="bold"
            size="medium"
          >
            {t('You have no Receipts added yet.')}
          </Inscription>
        </Box>
      );
    }

    return receipts?.map((receipt) => (
      <ReceiptsListItem
        key={receipt?.id}
        id={receipt?.id}
        fileName={receipt?.fileName}
        providerName={receipt?.providerName}
        createdAt={receipt?.createdAt}
        claimId={receipt?.claimId}
        documentId={receipt?.documentId}
        fileUrl={receipt?.fileUrl}
        onView={handleView}
        onDelete={handleDelete}
      />
    ));
  }, [isReceiptsLoading, receipts, isMobile, t, handleView, handleDelete]);

  useEffect(() => {
    setPage(1);
  }, [setPage, filtersValues.searchString, usage]);

  return (
    <Box
      data-testId="receipts-list_wrapper"
      pad={isMobile ? 'spacing16' : 'spacing24'}
      background="module"
      round="container1Round"
      {...isMobile ? { margin: { horizontal: 'spacing2' } } : {}}
    >
      {isMobile ? (
        <Box direction="column">
          <Box direction="row" align="center" justify="between">
            <Text weight={700} data-testId="receipts-list_total">
              {t('Files')}: {total}
            </Text>

            <Box gap="spacing12" direction="row" align="center">
              <AppButton buttonType="primary" style={{ width: '172px' }}>
                {t('Upload')}
              </AppButton>
            </Box>
          </Box>
          <Box margin={{ top: 'spacing16' }}>
            <SearchInput
              testId="receipts-list"
              value={filtersValues.searchString}
              onChange={setFilterValue.searchString}
              placeholder={t('Search with AI')}
              aiSearchMode
              hasSearchingHistory
            />
          </Box>
          <Box margin={{ top: 'spacing12' }}>
            <SelectDropdown
              id="receipts-usage"
              testId="receipts-list_usage-filter"
              name={t('Usage')}
              prefix={t('Usage')}
              value={usage}
              onChange={(value) => setUsage(value as ReceiptsUsage)}
              options={USAGE_OPTIONS}
              activeTitle
              singleMode
            />
          </Box>
        </Box>
      ) : (
        <Box direction="row" align="center" justify="between">
          <Text weight={700} data-testId="receipts-list_total">
            {t('Files')}: {total}
          </Text>

          <Box gap="spacing12" direction="row" align="center">
            <Box width={{ min: '305px' }}>
              <SearchInput
                testId="receipts-list"
                value={filtersValues.searchString}
                onChange={setFilterValue.searchString}
                placeholder={t('Search with AI')}
                aiSearchMode
                hasSearchingHistory
              />
            </Box>

            <Box width="200px">
              <SelectDropdown
                id="receipts-usage"
                testId="receipts-list_usage-filter"
                name={t('Usage')}
                prefix={t('Usage')}
                value={usage}
                onChange={(value) => setUsage(value as ReceiptsUsage)}
                options={USAGE_OPTIONS}
                activeTitle
                singleMode
              />
            </Box>

            <AppButton buttonType="primary" style={{ minWidth: '120px' }}>
              {t('Upload')}
            </AppButton>
          </Box>
        </Box>
      )}

      <Box margin={{ top: isMobile ? 'spacing8' : 'spacing24' }}>
        <List>
          <Responsive sizes={[Responsive.Desktop, Responsive.Tablet]}>
            <List.Header>
              <List.Header.Item flex={0.6}></List.Header.Item>
              <List.Header.Item flex={1.5}>{t('File name')}</List.Header.Item>
              <List.Header.Item flex={1.5}>{t('Upload date')}</List.Header.Item>
              <List.Header.Item flex={1}>{t('Linked claims')}</List.Header.Item>
              <List.Header.Item flex={0.4} textAlign="end">
                {t('Actions')}
              </List.Header.Item>
            </List.Header>
          </Responsive>
          {children}
        </List>
      </Box>

      <PaginationBar
        total={total}
        page={page}
        onChangePage={setPage}
        pageSize={perPage}
        onChangePageSize={setPerPage}
      />
      <ReceiptDeleteModal deletedItem={deletedItem} onSetId={setDeleteDocumentId} />
    </Box>
  );
};

export default ReceiptsList;
